import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import './Quotes.css';
import { formatAppDate } from '../../utils/dateUtils';

const QuotesList = forwardRef(({ onSelectQuote, selectedQuoteId, onNew }, ref) => {
  const { t } = useTranslation('sales');
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchQuotes
  }));

  useEffect(() => {
    fetchQuotes();
  }, [statusFilter]);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl('/api/quotes'));
      if (response.ok) {
        const data = await response.json();
        setQuotes(data.quotes || []);
      } else {
        setError(t('quotations.list.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setError(t('quotations.list.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = (quote.quotation_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          quote.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          quote.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          quote.reference?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusClass = status === 'ACCEPTED' ? 'status-completed' :
                       status === 'PENDING' ? 'status-active' :
                       status === 'DRAFT' ? 'status-pending' :
                       status === 'REJECTED' ? 'status-cancelled' :
                       status === 'EXPIRED' ? 'status-inactive' :
                       status === 'CONVERTED' ? 'status-success' :
                       status === 'PARTIALLY_CONVERTED' ? 'status-warning' :
                       status === 'FULLY_CONVERTED' ? 'status-success' : 'status-pending';

    const statusText = status === 'ACCEPTED' ? t('quotations.view.status.accepted') :
                      status === 'PENDING' ? t('quotations.view.status.pending') :
                      status === 'SENT' ? t('quotations.view.status.sent') :
                      status === 'DRAFT' ? t('quotations.view.status.draft') :
                      status === 'REJECTED' ? t('quotations.view.status.rejected') :
                      status === 'EXPIRED' ? t('quotations.view.status.expired') :
                      status === 'CONVERTED' ? t('quotations.view.status.converted') :
                      status === 'PARTIALLY_CONVERTED' ? t('quotations.view.status.partiallyConverted') :
                      status === 'FULLY_CONVERTED' ? t('quotations.view.status.fullyConverted') : t('quotations.view.status.draft');

    return <span className={`status-badge ${statusClass}`}>{statusText}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('quotations.view.defaultValues.notAvailable');
    return formatAppDate(dateString);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && quotes.length === 0) {
    return <div className="list-container"><div className="loading">{t('quotations.list.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('quotations.list.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('quotations.toolbar.buttons.newQuotation')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('quotations.list.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('quotations.list.filters.allStatus')}</option>
            <option value="DRAFT">{t('quotations.view.status.draft')}</option>
            <option value="PENDING">{t('quotations.view.status.pending')}</option>
            <option value="ACCEPTED">{t('quotations.view.status.accepted')}</option>
            <option value="REJECTED">{t('quotations.view.status.rejected')}</option>
            <option value="EXPIRED">{t('quotations.view.status.expired')}</option>
            <option value="CONVERTED">{t('quotations.view.status.converted')}</option>
            <option value="PARTIALLY_CONVERTED">{t('quotations.view.status.partiallyConverted')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('quotations.list.headers.quoteNumber')}</th>
              <th>{t('quotations.list.headers.customer')}</th>
              <th>{t('quotations.list.headers.totalAmount')}</th>
              <th>{t('quotations.list.headers.quoteDate')}</th>
              <th>{t('quotations.list.headers.validUntil')}</th>
              <th>{t('quotations.list.headers.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredQuotes.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  {searchTerm ? t('quotations.list.noData.noResults') : t('quotations.list.noData.noQuotations')}
                </td>
              </tr>
            ) : (
              filteredQuotes.map(quote => (
                <tr
                  key={quote.id}
                  onClick={() => onSelectQuote(quote.id)}
                  className={selectedQuoteId === quote.id ? 'selected' : ''}
                >
                  <td className="customer-code">
                    <code>{quote.quotation_number || t('quotations.view.defaultValues.notAvailable')}</code>
                  </td>
                  <td className="customer-name">
                    <strong>{quote.customer_name || t('quotations.list.noData.noCustomer')}</strong>
                  </td>
                  <td className="text-right">
                    {formatCurrency(quote.total_amount)}
                  </td>
                  <td>
                    {formatDate(quote.quotation_date)}
                  </td>
                  <td>
                    {formatDate(quote.valid_until_date)}
                  </td>
                  <td>
                    {getStatusBadge(quote.status)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
          {t('quotations.list.footer.recordCount', { filtered: filteredQuotes.length, total: quotes.length })}
        </div>
      </div>
    </div>
  );
});

QuotesList.displayName = 'QuotesList';

export default QuotesList;